import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Logo } from "@/components/Common";
import { useSeo } from "@/lib/seo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useSeo({ title: "Verify your email | Launch to Income", description: "Confirm your email to activate your Launch to Income workspace.", path: "/verify", noindex: true });
  const [state, setState] = useState("verifying"); // verifying | success | error
  const [error, setError] = useState("");
  const done = useRef(false);

  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const token = params.get("token");
    if (!token) { setState("error"); setError("Missing verification token."); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setUser(data.user);
        setState("success");
        // Hard navigation so AuthProvider re-initializes with the freshly-issued
        // session cookie and the Protected wrapper does not bounce to /login.
        const dest = data.user.onboarding_complete ? "/dashboard" : "/onboarding";
        setTimeout(() => window.location.assign(dest), 1000);
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail));
        setState("error");
      }
    })();
  }, []); // eslint-disable-line

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-[#FDFCFE] px-6 text-center" data-testid="verify-email-page">
      <div className="mb-8"><Logo height="h-9" /></div>
      {state === "verifying" && (<><Loader2 className="animate-spin text-brand" size={30} /><p className="mt-4 text-slate-600">Confirming your email…</p></>)}
      {state === "success" && (
        <>
          <span className="grid h-14 w-14 place-items-center rounded-full bg-emerald-50"><CheckCircle2 size={28} className="text-emerald-500" /></span>
          <h1 className="mt-5 font-heading text-2xl font-semibold">Email confirmed</h1>
          <p className="mt-2 text-slate-600">Taking you to your workspace…</p>
        </>
      )}
      {state === "error" && (
        <>
          <span className="grid h-14 w-14 place-items-center rounded-2xl bg-amber-50"><AlertCircle size={26} className="text-amber-500" /></span>
          <h1 className="mt-5 font-heading text-2xl font-semibold">We couldn't confirm this link</h1>
          <p className="mt-2 max-w-md text-slate-600" data-testid="verify-error">{error}</p>
          <button data-testid="verify-back-login" onClick={() => navigate("/login")} className="mt-6 rounded-lg bg-brand px-6 py-3 text-sm font-medium text-white hover:bg-brand-hover">Back to sign in</button>
        </>
      )}
    </div>
  );
}
